import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getProductoBySlug, categoryLabel, buildStampImageAlt } from '../api/productos';
import { getResenasProducto, crearResena } from '../api/resenas';
import { getPedidos } from '../api/pedidos';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import PosterSelection from '../components/PosterSelection';
import StampCustomForm from '../components/StampCustomForm';
import PosterOptionsForm from '../components/PosterOptionsForm';
import FavoritoButton from '../components/FavoritoButton';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { getButtonStyle } from '../utils/stepButtonStyle';
import logo from '../logo.jpg';
import useSEO from '../hooks/useSEO';
import { trackEvent } from '../utils/analytics';
import { warmUpServer } from '../api/checkout';
import { SHIPPING_COST } from '../constants';
import './Home.css';
import './Carrito.css';

const SITE_URL = 'https://miestampita.com';

// Joins the last two words with a non-breaking space so the final line of
// the paragraph can never end in a single orphaned word, regardless of the
// container width (desktop or mobile).
const preventOrphanWord = (text) => text.replace(/ ([^ ]*)$/, ' $1');

const renderStars = (rating) => {
  const rounded = Math.round(rating || 0);
  return (
    <span className="product-reviews__stars-display">
      {[1, 2, 3, 4, 5].map((value) =>
        value <= rounded ? (
          <StarIcon key={value} fontSize="inherit" />
        ) : (
          <StarBorderIcon key={value} fontSize="inherit" />
        )
      )}
    </span>
  );
};

// Injects Product + BreadcrumbList JSON-LD for the current stamp so search
// engines can show rich results (price, image, breadcrumb) for each product
// page. Removes the script on unmount/stamp change to avoid stale data.
const useProductStructuredData = (poster, price, rating, reviews) => {
  useEffect(() => {
    if (!poster) return undefined;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Product',
          name: poster.title,
          image: poster.image,
          description: `Estampita de ${categoryLabel(poster.category)} "${poster.title}", diseño ilustrado en acuarela, personalizable, hecha a mano en Montevideo, Uruguay.`,
          category: categoryLabel(poster.category),
          ...(rating?.total > 0
            ? {
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: rating.promedio,
                  reviewCount: rating.total,
                },
                // Google pide aggregateRating y review juntos -- solo el
                // promedio no alcanza. Son las mismas reseñas aprobadas que
                // ya se muestran en la página, nunca texto inventado.
                review: (reviews || []).map((r) => ({
                  '@type': 'Review',
                  reviewRating: { '@type': 'Rating', ratingValue: r.rating },
                  author: { '@type': 'Person', name: r.usuario_nombre },
                  ...(r.comentario ? { reviewBody: r.comentario } : {}),
                  // El backend manda "YYYY-MM-DD HH:MM:SS" (formato MySQL,
                  // no ISO 8601 -- le falta la "T"); se recorta a solo
                  // fecha, que sí es un date-time válido para schema.org.
                  datePublished: r.creado_en?.slice(0, 10),
                })),
              }
            : {}),
          brand: { '@type': 'Brand', name: 'MiEstampita' },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'UYU',
            price: price > 0 ? price : (poster.pricePerUnit ?? 65),
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/personalizar/${poster.slug}`,
            areaServed: 'UY',
            // Producto personalizado: no se aceptan devoluciones salvo error
            // nuestro (mismo criterio que el FAQ) -- no hay una categoría de
            // schema.org para "solo si es error del vendedor", así que se
            // declara como no permitidas en el caso general.
            hasMerchantReturnPolicy: {
              '@type': 'MerchantReturnPolicy',
              returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
              applicableCountry: 'UY',
            },
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: { '@type': 'MonetaryAmount', value: String(SHIPPING_COST), currency: 'UYU' },
              shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'UY' },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                transitTime: { '@type': 'QuantitativeValue', minValue: 5, maxValue: 10, unitCode: 'DAY' },
              },
            },
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Colección', item: `${SITE_URL}/coleccion` },
            { '@type': 'ListItem', position: 3, name: poster.title, item: `${SITE_URL}/personalizar/${poster.slug}` },
          ],
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [poster, price, rating?.total, rating?.promedio, reviews]);
};

const Customize = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isLoggedIn } = useAuth();
  const [baseStamp, setBaseStamp] = useState(null);
  // 'loading' | 'ready' | 'not-found' | 'error'
  const [loadState, setLoadState] = useState('loading');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');

    getProductoBySlug(slug)
      .then((data) => {
        if (cancelled) return;
        setBaseStamp(data);
        setLoadState('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadState(error?.message === 'Producto no encontrado' ? 'not-found' : 'error');
      });

    return () => {
      cancelled = true;
    };
  }, [slug, reloadKey]);

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  // La impresión siempre es a dos lados; ya no se puede elegir un solo lado.
  const printSide = 'double';
  const [selectionConfirmed, setSelectionConfirmed] = useState(false);
  const [stampFormData, setStampFormData] = useState({
    genero: '',
    name: '',
    date: '',
    text: '',
    capilla: '',
    madrina: '',
    padrino: '',
    isSaved: false,
    isValid: false,
  });
  const [expanded, setExpanded] = useState('step1');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addToCartError, setAddToCartError] = useState('');
  const galleryRef = useRef(null);

  const scrollGallery = (direction) => {
    const gallery = galleryRef.current;
    if (!gallery) return;
    gallery.scrollBy({ left: direction * gallery.clientWidth, behavior: 'smooth' });
  };

  const [resenas, setResenas] = useState([]);
  const [resenasTotal, setResenasTotal] = useState(0);
  const [resenasPromedio, setResenasPromedio] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHoverRating, setReviewHoverRating] = useState(0);
  const [reviewComentario, setReviewComentario] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState('');
  const [canReview, setCanReview] = useState(false);

  const productoId = baseStamp?.id ?? null;

  useEffect(() => {
    if (!productoId) return undefined;
    let cancelled = false;

    getResenasProducto(productoId)
      .then((data) => {
        if (cancelled) return;
        setResenas(data.resenas || []);
        setResenasTotal(data.total || 0);
        setResenasPromedio(data.promedio || null);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [productoId]);

  // El formulario de reseña solo se muestra si el usuario logueado
  // efectivamente compró y recibió este producto -- mismo criterio que
  // valida el backend en ResenaController::store, chequeado acá contra
  // GET /pedidos (ya trae items con producto_id) para no mostrar el
  // form a quien de todas formas lo va a recibir rechazado.
  useEffect(() => {
    if (!isLoggedIn || !productoId) {
      setCanReview(false);
      return undefined;
    }
    let cancelled = false;

    getPedidos()
      .then((pedidos) => {
        if (cancelled) return;
        const yaCompro = (pedidos || []).some(
          (pedido) =>
            pedido.estado === 'entregado' &&
            (pedido.items || []).some((item) => item.producto_id === productoId)
        );
        setCanReview(yaCompro);
      })
      .catch(() => setCanReview(false));

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, productoId]);

  const handleSubmitResena = async (event) => {
    event.preventDefault();
    if (!reviewRating) {
      setReviewError('Elegí una cantidad de estrellas');
      return;
    }
    setReviewSubmitting(true);
    setReviewError('');
    setReviewSuccess('');
    try {
      const data = await crearResena({
        productoId,
        rating: reviewRating,
        comentario: reviewComentario.trim(),
      });
      setReviewSuccess(data.mensaje || 'Gracias por tu reseña.');
      setReviewRating(0);
      setReviewComentario('');
    } catch (error) {
      setReviewError(error.message);
    } finally {
      setReviewSubmitting(false);
    }
  };

  useEffect(() => {
    if (lightboxIndex === null) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setLightboxIndex(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex]);

  const selectedPoster = baseStamp ? { ...baseStamp, selectedVariant } : null;
  const selectedOption =
    selectedPoster && selectedOptionIndex !== null
      ? selectedPoster.options[selectedOptionIndex]
      : null;

  const getCopiesFromOption = (option) => {
    const match = option?.type?.match(/(\d+)/);
    return match ? Number(match[1]) : 0;
  };

  const copies = getCopiesFromOption(selectedOption);
  const pricePerStamp = selectedPoster?.pricePerUnit ?? 65;
  const calculatedPrice = copies > 0 ? copies * pricePerStamp : 0;

  const categoryLabelText = selectedPoster ? categoryLabel(selectedPoster.category) : '';
  // Título distinguible mientras carga, para que scripts/prerender.js pueda
  // esperar a que termine de resolver antes de capturar el HTML final.
  useSEO({
    title:
      loadState === 'loading'
        ? 'Cargando estampita... | MiEstampita'
        : selectedPoster
          ? `Estampita ${selectedPoster.title} para ${categoryLabelText} | MiEstampita`
          : 'Diseño no encontrado | MiEstampita',
    description:
      loadState === 'loading'
        ? 'Cargando información de la estampita.'
        : selectedPoster
          ? `Personalizá la estampita "${selectedPoster.title}" para ${categoryLabelText.toLowerCase()}: elegí la cantidad, agregá el nombre, la fecha y la oración. Hecha a mano en Montevideo, Uruguay, con envíos a Punta del Este y todo el país.`
          : 'No encontramos ese diseño de estampita.',
    path: `/personalizar/${slug}`,
    noindex: !selectedPoster,
  });
  useProductStructuredData(
    selectedPoster,
    calculatedPrice,
    { total: resenasTotal, promedio: resenasPromedio },
    resenas,
  );

  const handlePrintOptionChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setSelectedOptionIndex(null);
      return;
    }

    if (!selectedPoster) return;
    const newIndex = Number(value);
    setSelectedOptionIndex(newIndex);
    setSelectedVariant(selectedPoster.options[newIndex]);
  };

  const isStep1Enabled = !!selectedPoster?.selectedVariant;
  const isStep2Enabled = selectionConfirmed;
  const isStep3Enabled = isStep2Enabled && stampFormData.isSaved;

  const handleChangeAccordion = (panel) => (event, isExpanded) => {
    const allowExpand =
      (panel === 'step1') ||
      (panel === 'step2' && isStep2Enabled) ||
      (panel === 'step3' && isStep3Enabled);

    if (!allowExpand) return;
    setExpanded(isExpanded ? panel : false);
  };

  const handleAgregarAlCarrito = async () => {
    setAddToCartError('');
    setAddingToCart(true);
    try {
      await addItem({
        productoId: selectedPoster.id,
        cantidad: copies,
        personalizacion: {
          genero: stampFormData.genero,
          nombre: stampFormData.name,
          fecha: stampFormData.date,
          oracion: stampFormData.text,
          capilla: stampFormData.capilla,
          madrina: stampFormData.madrina,
          padrino: stampFormData.padrino,
        },
      });
      warmUpServer();
      trackEvent('add_to_cart', {
        currency: 'UYU',
        value: calculatedPrice,
        items: [
          {
            item_id: selectedPoster.slug,
            item_name: selectedPoster.title,
            item_category: selectedPoster.category,
            price: pricePerStamp,
            quantity: copies,
          },
        ],
      });
      navigate('/carrito');
    } catch (error) {
      setAddToCartError(error.message || 'No se pudo agregar al carrito.');
    } finally {
      setAddingToCart(false);
    }
  };

  if (loadState === 'loading') {
    return (
      <div className="home-page">
        <div className="home-wrapper">
          <p>Cargando estampita...</p>
        </div>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="home-page">
        <div className="home-wrapper">
          <p>
            No pudimos cargar este diseño.{' '}
            <button type="button" className="retry-link" onClick={() => setReloadKey((key) => key + 1)}>
              Reintentar
            </button>
          </p>
          <Link to="/coleccion">Volver a la colección</Link>
        </div>
      </div>
    );
  }

  if (!selectedPoster) {
    return (
      <div className="home-page">
        <div className="home-wrapper">
          <p>No encontramos ese diseño.</p>
          <Link to="/coleccion">Volver a la colección</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="logo-wrapper">
        <Link to="/">
          <img src={logo} alt="MiEstampita Logo" className="logo" />
        </Link>
      </div>
      <div className="home-wrapper">
        <Link to="/coleccion" className="back-to-collection">
          ‹ Volver a la colección
        </Link>

        <div className="selected-stamp-preview">
          <h1 className="selected-title">Seleccionaste: {selectedPoster.title}</h1>
          <div className="selected-stamp-preview__favorito">
            <FavoritoButton productoId={selectedPoster.id} />
          </div>
          <div className="selected-stamp-preview__gallery-wrapper">
            {selectedPoster.images.length > 1 && (
              <button
                type="button"
                className="selected-stamp-preview__arrow selected-stamp-preview__arrow--prev"
                onClick={() => scrollGallery(-1)}
                aria-label="Imagen anterior"
              >
                <ChevronLeftIcon />
              </button>
            )}
            <div className="selected-stamp-preview__gallery" ref={galleryRef}>
              {selectedPoster.images.map((src, index) => (
                <button
                  key={src}
                  type="button"
                  className="selected-stamp-preview__image-button"
                  onClick={() => setLightboxIndex(index)}
                  aria-label={`Ampliar imagen: ${buildStampImageAlt(selectedPoster, index)}`}
                >
                  <img
                    src={src}
                    alt={buildStampImageAlt(selectedPoster, index)}
                    className="selected-stamp-preview__image"
                  />
                </button>
              ))}
            </div>
            {selectedPoster.images.length > 1 && (
              <button
                type="button"
                className="selected-stamp-preview__arrow selected-stamp-preview__arrow--next"
                onClick={() => scrollGallery(1)}
                aria-label="Imagen siguiente"
              >
                <ChevronRightIcon />
              </button>
            )}
          </div>
          {selectedPoster.description && (
            <p className="selected-stamp-preview__description">
              {preventOrphanWord(selectedPoster.description)}
            </p>
          )}

          <div className="product-reviews">
            <h2 className="product-reviews__title">Reseñas</h2>
            {resenasTotal > 0 ? (
              <p className="product-reviews__summary">
                {renderStars(resenasPromedio)}
                <span>
                  {resenasPromedio} · {resenasTotal} reseña{resenasTotal === 1 ? '' : 's'}
                </span>
              </p>
            ) : (
              <p className="product-reviews__summary product-reviews__summary--empty">
                Todavía no hay reseñas para este producto.
              </p>
            )}

            {resenas.length > 0 && (
              <ul className="product-reviews__list">
                {resenas.map((r) => (
                  <li key={r.id} className="product-reviews__item">
                    <div className="product-reviews__item-header">
                      {renderStars(r.rating)}
                      <span className="product-reviews__author">{r.usuario_nombre}</span>
                    </div>
                    {r.comentario && <p className="product-reviews__comment">{r.comentario}</p>}
                  </li>
                ))}
              </ul>
            )}

            {canReview && (
              <form onSubmit={handleSubmitResena} className="product-reviews__form">
                <p className="product-reviews__form-label">Dejá tu reseña</p>
                <div className="product-reviews__star-picker">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="product-reviews__star-button"
                      onClick={() => setReviewRating(value)}
                      onMouseEnter={() => setReviewHoverRating(value)}
                      onMouseLeave={() => setReviewHoverRating(0)}
                      aria-label={`${value} estrella${value === 1 ? '' : 's'}`}
                    >
                      {(reviewHoverRating || reviewRating) >= value ? <StarIcon /> : <StarBorderIcon />}
                    </button>
                  ))}
                </div>
                <textarea
                  className="product-reviews__textarea"
                  placeholder="Contanos tu experiencia (opcional)"
                  value={reviewComentario}
                  onChange={(event) => setReviewComentario(event.target.value)}
                  rows={3}
                />
                {reviewError && <p className="product-reviews__error">{reviewError}</p>}
                {reviewSuccess && <p className="product-reviews__success">{reviewSuccess}</p>}
                <button type="submit" className="product-reviews__submit" disabled={reviewSubmitting}>
                  {reviewSubmitting ? 'Enviando...' : 'Enviar reseña'}
                </button>
              </form>
            )}
          </div>
        </div>

        {lightboxIndex !== null && (
          <div
            className="image-lightbox"
            onClick={() => setLightboxIndex(null)}
            role="presentation"
          >
            <button
              type="button"
              className="image-lightbox__close"
              onClick={() => setLightboxIndex(null)}
              aria-label="Cerrar imagen ampliada"
            >
              ×
            </button>
            <img
              src={selectedPoster.images[lightboxIndex]}
              alt={buildStampImageAlt(selectedPoster, lightboxIndex)}
              className="image-lightbox__image"
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        )}

        {/* Paso 1 */}
        <Accordion expanded={expanded === 'step1'} onChange={handleChangeAccordion('step1')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>1. Selecciona cantidad de estampitas</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PosterSelection
              selectedStamp={selectedPoster}
              selectedOptionIndex={selectedOptionIndex}
              onPrintOptionChange={handlePrintOptionChange}
              selectedOption={selectedOption}
              calculatedPrice={calculatedPrice}
              copies={copies}
            />
            <button
              className="step-button"
              style={getButtonStyle(isStep1Enabled)}
              onClick={() => {
                setSelectionConfirmed(true);
                setExpanded('step2');
                trackEvent('personalizacion_iniciada', {
                  item_id: selectedPoster.slug,
                  item_name: selectedPoster.title,
                  item_category: selectedPoster.category,
                  quantity: copies,
                });
              }}
              disabled={!isStep1Enabled}
            >
              Siguiente
            </button>
          </AccordionDetails>
        </Accordion>

        {/* Paso 2 */}
        <Accordion expanded={expanded === 'step2'} onChange={handleChangeAccordion('step2')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: !isStep2Enabled ? 'gray' : 'inherit' }}>
            <Typography>2. Personaliza tu estampita</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <StampCustomForm
              onChange={setStampFormData}
              selectedCategory={selectedPoster.category}
              onSubmit={(form) => {
                setStampFormData({ ...form, isSaved: true, isValid: true });
                setExpanded('step3');
                trackEvent('personalizacion_completada', {
                  item_id: selectedPoster.slug,
                  item_name: selectedPoster.title,
                  item_category: selectedPoster.category,
                });
              }}
            />
            <button
              className="step-button"
              style={getButtonStyle(isStep2Enabled && stampFormData.isValid)}
              type="submit"
              form="stamp-custom-form"
              disabled={!isStep2Enabled || !stampFormData.isValid}
            >
              Siguiente
            </button>
          </AccordionDetails>
        </Accordion>

        {/* Paso 3 */}
        <Accordion expanded={expanded === 'step3'} onChange={handleChangeAccordion('step3')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ color: !isStep3Enabled ? 'gray' : 'inherit' }}>
            <Typography>3. Previsualizacion</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PosterOptionsForm
              poster={selectedPoster}
              stampFormData={stampFormData}
              printSide={printSide}
            />
            <div className="step3-actions step3-actions-double">
              <button
                className="step-button"
                style={getButtonStyle(true)}
                onClick={() => setExpanded('step2')}
              >
                Modificar datos
              </button>
              <button
                className="step-button"
                style={{
                  ...getButtonStyle(!addingToCart),
                  backgroundColor: '#16a34a',
                  border: '1px solid #16a34a',
                }}
                onClick={handleAgregarAlCarrito}
                disabled={addingToCart}
              >
                {addingToCart ? 'Agregando...' : 'Agregar al Carrito'}
              </button>
            </div>
            {addToCartError && <p className="cart-cupon__error">{addToCartError}</p>}
            <div className="image-note">
              <h4>Nota sobre las imágenes</h4>
              <p>
                Las fotografías de esta tienda son ilustrativas y buscan representar cada
                producto con la mayor fidelidad posible.
              </p>
              <p>
                Debido a las diferencias entre las pantallas y los procesos de impresión, es
                posible que existan ligeras variaciones en los colores o pequeños detalles del
                diseño respecto al producto recibido. Estas diferencias son naturales y no
                afectan la calidad ni la esencia de cada pieza.
              </p>
              <p>
                Cada pedido es elaborado con el mismo cuidado y atención que inspira nuestras
                ilustraciones.
              </p>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};

export default Customize;
